/**
 * Helpers for objects keyed by a coerced finite numeric `id` (e.g. crew / user rows).
 */

/**
 * Unique finite numeric ids from a list (via `Number` + `Number.isFinite`).
 *
 * @param {Array<Object>|null|undefined} items
 * @param {string} [idKey='id']
 * @returns {Set<number>}
 */
export function getFiniteNumericIdSet(items, idKey = "id") {
  const set = new Set();
  for (const item of items ?? []) {
    const n = Number(item?.[idKey]);
    if (Number.isFinite(n)) {
      set.add(n);
    }
  }
  return set;
}

/**
 * Map each item to `Number(id)` when finite; later entries overwrite earlier ones for the same key.
 *
 * @template T
 * @param {Array<T>|null|undefined} items
 * @param {string} [idKey='id']
 * @returns {Map<number, T>}
 */
export function buildMapByNumericId(items, idKey = "id") {
  const map = new Map();
  if (!Array.isArray(items)) {
    return map;
  }
  for (const item of items) {
    const n = Number(item?.[idKey]);
    if (Number.isFinite(n)) {
      map.set(n, item);
    }
  }
  return map;
}

/**
 * Resolve each id through {@link buildMapByNumericId} in list order; omits missing entries.
 *
 * @template T
 * @param {Map<number, T>} map
 * @param {Array<number|string|null|undefined>|null|undefined} ids
 * @returns {Array<T>}
 */
export function pickItemsByNumericIdsInOrder(map, ids) {
  if (!Array.isArray(ids)) {
    return [];
  }
  const out = [];
  for (const id of ids) {
    const n = Number(id);
    if (Number.isFinite(n)) {
      const item = map.get(n);
      if (item !== undefined) {
        out.push(item);
      }
    }
  }
  return out;
}
